import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  Upload,
  Download,
  Trash2,
  AlertCircle,
  FileText,
  Eye,
} from "lucide-react";
import axios from "axios";

interface DocumentManagerProps {
  label: string;
  field: string;
  existingDocument?: string | null;
  documentId?: string | null; // For display (invoice number, document ID, etc.)
  newFile?: File | null;
  onFileChange: (field: string, file: File | null) => void;
  onDeleteDocument?: (field: string) => void;
  onUploadSuccess?: (field: string, result: any) => void;
  accept?: string;
  isLoading?: boolean;
  error?: string;
  uploadEndpoint?: string;
  deleteEndpoint?: string;
  customUploadData?: Record<string, string>; // For additional form data like milestoneId, clientName, etc.
  uploadMode?: "auto" | "manual"; // auto uploads immediately, manual waits for form submission
  documentType?: "invoice" | "document"; // for different styling/behavior
  maxSizeMessage?: string;
  supportedFormatsMessage?: string;
}

const DocumentManager: React.FC<DocumentManagerProps> = ({
  label,
  field,
  existingDocument,
  documentId,
  newFile,
  onFileChange,
  onDeleteDocument,
  onUploadSuccess,
  accept = ".pdf,.jpg,.jpeg,.png",
  isLoading = false,
  error,
  uploadEndpoint,
  deleteEndpoint,
  customUploadData = {},
  uploadMode = "manual",
  documentType = "document",
  maxSizeMessage = "Maximum file size: 10MB",
  supportedFormatsMessage = "Supported formats: PDF, JPG, PNG",
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Generate document ID from existing URL, provided ID, or file name
  const getDocumentId = () => {
    if (documentId) return documentId;
    if (existingDocument) {
      const urlParts = existingDocument.split("/");
      const fileName = urlParts[urlParts.length - 1];
      return fileName.split(".")[0] || `DOC-${field.toUpperCase()}`;
    }
    if (newFile) {
      return newFile.name.split(".")[0];
    }
    return null;
  };

  const validateFile = (file: File): string | null => {
    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      return "File size must be less than 10MB";
    }

    // Check file type for invoices (PDF only)
    if (documentType === "invoice" && file.type !== "application/pdf") {
      return "Only PDF files are allowed for invoices";
    }

    return null;
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      onFileChange(field, null);
      return;
    }

    if (uploadMode === "auto" && uploadEndpoint) {
      // Auto upload mode (for invoices)
      await handleAutoUpload(file);
    } else {
      // Manual mode (for employee documents)
      setIsUploading(true);
      setTimeout(() => {
        onFileChange(field, file);
        setIsUploading(false);
      }, 500);
    }
  };

  const handleAutoUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append(field, file);

      // Add custom upload data
      Object.entries(customUploadData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const token = localStorage.getItem("authToken");
      const response = await axios.post(uploadEndpoint!, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
      });

      if (response.data.success && onUploadSuccess) {
        onUploadSuccess(field, response.data.data);
      }
    } catch (error: any) {
      console.error("Error uploading file:", error);
      // Error will be handled by parent component
    } finally {
      setIsUploading(false);
    }
  };

  const handleViewDocument = async () => {
    if (existingDocument) {
      setIsViewing(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        window.open(existingDocument, "_blank");
      } finally {
        setTimeout(() => setIsViewing(false), 800);
      }
    }
  };

  const handleDownloadDocument = () => {
    if (existingDocument) {
      const link = document.createElement("a");
      link.href = existingDocument;
      link.download = `${label.replace(/\s+/g, "_").toLowerCase()}_document`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteDocument = async () => {
    if (deleteEndpoint) {
      // Auto delete mode (for invoices)
      setIsDeleting(true);
      try {
        const token = localStorage.getItem("authToken");
        await axios.delete(deleteEndpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (onDeleteDocument) {
          onDeleteDocument(field);
        }
      } catch (error) {
        console.error("Error deleting document:", error);
      } finally {
        setIsDeleting(false);
      }
    } else if (onDeleteDocument) {
      // Manual delete mode (for employee documents)
      setIsDeleting(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        onDeleteDocument(field);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleReplaceDocument = () => {
    document.getElementById(`${field}-file-input`)?.click();
  };

  const hasDocument = existingDocument || newFile;
  const displayId = getDocumentId();

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Document Label */}
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">{label}</Label>
          </div>

          {/* Document Display or Upload Area */}
          {hasDocument ? (
            <div className="space-y-4">
              {/* Document ID */}
              {displayId && (
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {documentType === "invoice"
                      ? "Invoice Number"
                      : "Document ID"}
                  </Label>
                  <div className="text-sm font-mono text-foreground">
                    {displayId}
                  </div>
                </div>
              )}

              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                    newFile
                      ? "bg-blue-100 text-blue-800"
                      : existingDocument
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {newFile
                    ? "New File Selected"
                    : existingDocument
                    ? documentType === "invoice"
                      ? "Invoice Uploaded"
                      : "Document Uploaded"
                    : "Pending Upload"}
                </span>
                {newFile && (
                  <span className="text-xs text-muted-foreground">
                    Size: {(newFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {existingDocument && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleViewDocument}
                    disabled={isViewing || isLoading}
                    className="flex-1"
                  >
                    {isViewing ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <Eye className="mr-2 h-4 w-4" />
                        View{" "}
                        {documentType === "invoice" ? "Invoice" : "Document"}
                      </>
                    )}
                  </Button>
                )}

                {existingDocument && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDownloadDocument}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                )}

                {(onDeleteDocument || deleteEndpoint) && existingDocument && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDeleteDocument}
                    disabled={isDeleting || isLoading}
                    className="text-red-600 hover:text-red-700"
                  >
                    {isDeleting ? (
                      <Spinner size="sm" className="h-4 w-4" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>

              {/* Replace Document Section */}
              <div className="pt-2 border-t">
                <Label className="text-xs text-muted-foreground">
                  {newFile
                    ? "Replace Selected File"
                    : `Replace ${
                        documentType === "invoice" ? "Invoice" : "Document"
                      }`}
                </Label>
                <div className="mt-1">
                  <input
                    id={`${field}-file-input`}
                    type="file"
                    accept={accept}
                    onChange={handleFileInputChange}
                    disabled={isUploading || isLoading}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleReplaceDocument}
                    disabled={isUploading || isLoading}
                  >
                    {isUploading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Replace
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Upload Area for New Document */
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                No {documentType === "invoice" ? "invoice" : "document"}{" "}
                uploaded
              </div>

              <div>
                <input
                  id={`${field}-file-input`}
                  type="file"
                  accept={accept}
                  onChange={handleFileInputChange}
                  disabled={isUploading || isLoading}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReplaceDocument}
                  disabled={isUploading || isLoading}
                  className="w-full"
                >
                  {isUploading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload {label}
                    </>
                  )}
                </Button>
              </div>

              <div className="text-xs text-muted-foreground">
                <p>• {supportedFormatsMessage}</p>
                <p>• {maxSizeMessage}</p>
                {documentType === "invoice" && (
                  <p>
                    • File will be saved as:
                    clients/[client]/[project]/invoice_[milestone].pdf
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* New File Info */}
          {newFile && (
            <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded">
              New file selected: {newFile.name}
              {existingDocument &&
                ` - This will replace the current ${
                  documentType === "invoice" ? "invoice" : "document"
                } when saved.`}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentManager;
